// Pure helpers for the Drive ingestion UI. No imports so they run under `deno test`.

export type Provider = 'google' | 'microsoft';

export const FILE_TYPES = ['pdf', 'docx', 'pptx', 'xlsx', 'csv'] as const;
export type FileType = (typeof FILE_TYPES)[number];

/** Error from the drive-ingest edge function; `code` is one of not_connected | needs_reauth | sync_busy | folder_not_accessible. */
export class DriveIngestError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'DriveIngestError';
    this.code = code;
    this.status = status;
  }
}

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Builds an error from a parsed JSON error body (`{error, code?}`); anything else becomes a generic message. */
export function errorFromBody(body: unknown, status?: number): DriveIngestError {
  const b = (body && typeof body === 'object' ? body : {}) as { error?: unknown; code?: unknown };
  const message = typeof b.error === 'string' && b.error.length > 0 ? b.error : GENERIC_ERROR;
  return new DriveIngestError(message, typeof b.code === 'string' ? b.code : undefined, status);
}

/** Only ever navigate the browser to Google's consent screen. */
export function isGoogleAuthUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && u.hostname === 'accounts.google.com';
  } catch {
    return false;
  }
}

export function statusLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  switch (status) {
    case 'needs_reauth': return 'Reconnect needed';
    case 'success': return 'Synced';
    case 'partial': return 'Partially synced';
    case 'running': return 'Syncing';
    case 'failed': return 'Failed';
    default: return status;
  }
}

export type BadgeTone = 'default' | 'secondary' | 'destructive' | 'outline';
export function statusTone(status: string | null | undefined): BadgeTone {
  if (status === 'failed' || status === 'needs_reauth') return 'destructive';
  if (status === 'success') return 'default';
  return 'secondary';
}

/** Keeps only known types, in canonical order, without duplicates. */
export function normalizeFileTypes(selected: readonly string[]): FileType[] {
  return FILE_TYPES.filter((t) => selected.includes(t));
}

export function folderPath(stack: ReadonlyArray<{ name: string }>): string {
  return stack.map((s) => s.name).join(' / ');
}

/** Message for a failed sync trigger, special-casing the "already running" outcome. */
export function syncErrorMessage(e: unknown): string {
  if (e instanceof DriveIngestError && e.code === 'sync_busy') return 'A sync is already running. Try again in a moment.';
  return e instanceof Error ? e.message : GENERIC_ERROR;
}
